import { service } from '@ember/service';
import Route from '@ember/routing/route';
import {
  loadSelectedSchool,
  saveCoursesDate,
} from 'tio-common/utils/tuition-assistance/application-new-route';
import { getSingleValueForTasField } from 'tio-common/utils/tuition-assistance/fields';
import { findRecord } from '@warp-drive/utilities/json-api';
import { saveRecord } from 'tio-data/requests/save-record';
import { loadTasApplicationHistory } from 'tio-data/requests/tas-application-actions';
import type { IntlService } from 'ember-intl';
import type RouterService from '@ember/routing/router';
import type { Store } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';

export default class AuthenticatedTasApplicationsTypeFourGuidedNewRoute extends Route {
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare intl: IntlService;

  includes = [
    'tas-courses',
    'tas-courses.tas-assets.attachments',
    'tas-courses.tas-assets.asset-summary',
    'tas-program-instance',
    'tas-program-instance.tas-program-template.tas-groups',
    'tas-program-instance.tas-program-template.conditions.public-assets',
    'tas-program-instance.tas-program-template.tas-field-options',
    'tas-program-instance.employee.person',
    'tas-program-instance.employee.tas-participant',
    'tas-program-instance.employee.company',
    'tas-program-instance.employee.pcs-card-holders',
    'tas-program-instance.tas-program-template.company.company-setting',
    'tas-assets.attachments',
    'tas-assets.asset-summary',
    'tas-assistances',
    'comments',
    'tags',
  ];

  queryParams = {
    date: {
      refreshModel: false,
    },
  };

  async model({ tas_application_id, date = '' }: { tas_application_id: string; date?: string }) {
    // WarpDrive builder read (Phase 2.4) — replaces store.findRecord; reload: true maps to builder option.
    const { content } = (await this.store.request(
      findRecord<TasApplicationModel>('tas-application', tas_application_id, {
        include: this.includes.join(','),
        reload: true,
      })
    )) as { content: { data: TasApplicationModel } };
    const application = content.data;
    const programInstance = application.tasProgramInstance;

    if (date) {
      const lockStatus: string = getSingleValueForTasField(
        'LOCK_EMPLOYEE_STATUS_BASED_ON',
        programInstance.tasProgramTemplate.fields
      );
      if (saveCoursesDate(date, application, lockStatus)) {
        try {
          // WarpDrive mutation builder (Phase 2.7) — replaces application.save().
          await saveRecord(this.store, application);
        } catch (error) {
          console.error('Failed to save application, instance, or course:', error);
          application.rollbackAttributes();
        }
      }
    }

    const selectedSchool = await loadSelectedSchool(programInstance, this.store);

    try {
      await loadTasApplicationHistory(this.store, application);
    } catch (e) {
      // History is non-critical — don't block the route
      console.error('Error loading application history', e);
    }

    return {
      application,
      programInstance,
      selectedSchool,
    };
  }
}
